import React, { useState, useMemo, useEffect } from "react";
import { AppLayout, PageHeader } from "./_shared/AppLayout";
import { brl, filterByMonth, sumValor } from "./_shared/utils";
import { useAppData } from "./_shared/store";
import { catById } from "./_shared/categories";
import {
  Target, Sparkles, TrendingUp, Plus, Pencil, Trash2,
  X, Save, PlusCircle,
} from "lucide-react";
import { toast } from "sonner";

// ─── Constants ────────────────────────────────────────────────
const MONTH_NAMES = [
  "Janeiro","Fevereiro","Março","Abril","Maio","Junho",
  "Julho","Agosto","Setembro","Outubro","Novembro","Dezembro",
];
const NOW      = new Date();
const YEAR_NOW  = NOW.getFullYear();
const MONTH_NOW = NOW.getMonth() + 1;

const TEMPLATES = [
  { emoji: "🛡️", nome: "Reserva de Emergência", alvo: 46200, descricao: "6x os gastos mensais",    prioridade: "Alta"  },
  { emoji: "✈️", nome: "Viagem dos Sonhos",       alvo: 10000, descricao: "Sua próxima aventura",    prioridade: "Média" },
  { emoji: "🚗", nome: "Troca de Carro",           alvo: 30000, descricao: "Veículo novo ou seminovo", prioridade: "Média" },
];

const PRIORITY_COLOR = {
  "Alta":  "text-rose-300 bg-rose-500/20 border-rose-500/30",
  "Média": "text-amber-300 bg-amber-500/20 border-amber-500/30",
  "Baixa": "text-slate-300 bg-slate-500/20 border-slate-500/30",
};

const STATUS_CFG = {
  "Concluída":    { badge: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40", bar: "from-emerald-400 to-emerald-500", icon: "✅" },
  "Em andamento": { badge: "bg-amber-500/20 text-amber-300 border-amber-500/40",       bar: "from-emerald-400 to-blue-400",    icon: "🟡" },
  "Atrasada":     { badge: "bg-rose-500/20 text-rose-300 border-rose-500/40",           bar: "from-rose-400 to-rose-600",       icon: "❌" },
};

const EMOJIS = ["🎯","🛡️","✈️","🚗","🏠","💍","🎓","💻","🏖️","📈","💰","🐾"];

// ─── Helpers ──────────────────────────────────────────────────
const mesesRestantes = (prazoData) => {
  if (!prazoData) return null;
  const [y, m] = prazoData.split("-").map(Number);
  const diff = (y - YEAR_NOW) * 12 + (m - MONTH_NOW);
  return Math.max(0, diff);
};

const calcStatus = (o) => {
  if (Number(o.alvo) > 0 && Number(o.atual) >= Number(o.alvo)) return "Concluída";
  if (o.prazoData && mesesRestantes(o.prazoData) === 0) return "Atrasada";
  return "Em andamento";
};

const calcAporte = (o) => {
  const falta = Math.max(0, Number(o.alvo) - Number(o.atual));
  const meses = mesesRestantes(o.prazoData);
  if (!meses || meses <= 0) return falta;
  return falta / meses;
};

// ─── Nova Meta Modal ──────────────────────────────────────────
function NovaMetaModal({ open, onClose, item, onSave }) {
  const EMPTY = {
    nome: "", emoji: "🎯", alvo: "", atual: "",
    prazoMes: MONTH_NOW, prazoAno: YEAR_NOW + 1, prioridade: "Média",
  };
  const [form, setForm] = useState(EMPTY);

  useEffect(() => {
    if (!open) return;
    if (item) {
      const prazoData = item.prazoData || "";
      const [prazoAno, prazoMes] = prazoData
        ? prazoData.split("-").map(Number)
        : [YEAR_NOW + 1, MONTH_NOW];
      setForm({
        nome:       item.nome       || "",
        emoji:      item.emoji      || "🎯",
        alvo:       item.alvo       ? String(item.alvo)  : "",
        atual:      item.atual      ? String(item.atual) : "",
        prazoMes,
        prazoAno,
        prioridade: item.prioridade || "Média",
      });
    } else {
      setForm(EMPTY);
    }
  }, [open, item]); // eslint-disable-line

  const alvoN  = Number(String(form.alvo).replace(",", "."))  || 0;
  const atualN = Number(String(form.atual).replace(",", ".")) || 0;
  const prazoData  = `${form.prazoAno}-${String(form.prazoMes).padStart(2, "0")}`;
  const meses      = mesesRestantes(prazoData);
  const aporteCalc = alvoN > 0 && meses > 0 ? Math.max(0, alvoN - atualN) / meses : 0;

  const submit = (e) => {
    e.preventDefault();
    if (!form.nome.trim())    return toast.error("Informe o nome da meta.");
    if (!alvoN || alvoN <= 0) return toast.error("Valor alvo inválido.");
    onSave({ nome: form.nome.trim(), emoji: form.emoji, alvo: alvoN, atual: atualN, prazoData, prioridade: form.prioridade });
  };

  if (!open) return null;

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const years = Array.from({ length: 10 }, (_, i) => YEAR_NOW + i);

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div
        className="w-full max-w-md bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800">
          <div className="font-display font-bold text-lg">{item && item.id ? "Editar meta" : "Nova meta"}</div>
          <button onClick={onClose} className="h-8 w-8 grid place-items-center rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition">
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-4">
          {/* Emoji picker */}
          <div>
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider block mb-2">Ícone</label>
            <div className="flex flex-wrap gap-1.5 items-center">
              {EMOJIS.map((em) => (
                <button
                  key={em}
                  type="button"
                  onClick={() => set({ emoji: em })}
                  className={`h-9 w-9 rounded-xl text-lg grid place-items-center border transition ${
                    form.emoji === em
                      ? "bg-emerald-500/20 border-emerald-500/60"
                      : "bg-slate-800 border-slate-700 hover:border-slate-600"
                  }`}
                >
                  {em}
                </button>
              ))}
              <input
                type="text"
                value={form.emoji}
                onChange={(e) => set({ emoji: e.target.value.slice(0, 2) })}
                className="w-16 h-9 px-2 text-center rounded-xl bg-slate-800 border border-slate-700 text-sm text-white focus:outline-none focus:border-emerald-500/40"
                placeholder="ou…"
              />
            </div>
          </div>

          {/* Nome */}
          <div>
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider block mb-1.5">Nome da meta</label>
            <input
              value={form.nome}
              onChange={(e) => set({ nome: e.target.value })}
              placeholder="Ex: Viagem para Europa"
              className="w-full h-10 px-3 rounded-xl bg-slate-800 border border-slate-700 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-emerald-500/40"
            />
          </div>

          {/* Valores */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider block mb-1.5">Valor total (R$)</label>
              <input
                value={form.alvo}
                onChange={(e) => set({ alvo: e.target.value })}
                placeholder="10000"
                inputMode="decimal"
                className="w-full h-10 px-3 rounded-xl bg-slate-800 border border-slate-700 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-emerald-500/40"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider block mb-1.5">Já guardado (R$)</label>
              <input
                value={form.atual}
                onChange={(e) => set({ atual: e.target.value })}
                placeholder="0"
                inputMode="decimal"
                className="w-full h-10 px-3 rounded-xl bg-slate-800 border border-slate-700 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-emerald-500/40"
              />
            </div>
          </div>

          {/* Prazo */}
          <div>
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider block mb-1.5">Prazo para atingir</label>
            <div className="grid grid-cols-2 gap-2">
              <select
                value={form.prazoMes}
                onChange={(e) => set({ prazoMes: Number(e.target.value) })}
                className="h-10 px-3 rounded-xl bg-slate-800 border border-slate-700 text-sm text-white focus:outline-none"
              >
                {MONTH_NAMES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
              </select>
              <select
                value={form.prazoAno}
                onChange={(e) => set({ prazoAno: Number(e.target.value) })}
                className="h-10 px-3 rounded-xl bg-slate-800 border border-slate-700 text-sm text-white focus:outline-none"
              >
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>

          {/* Aporte sugerido (auto) */}
          {aporteCalc > 0 && (
            <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/20 px-4 py-3 flex items-center justify-between">
              <div className="text-xs text-emerald-300 font-semibold">Aporte mensal sugerido</div>
              <div className="text-sm font-bold text-emerald-300 tabular-nums">{brl(aporteCalc)}/mês</div>
            </div>
          )}

          {/* Prioridade */}
          <div>
            <label className="text-xs font-semibold text-slate-400 uppercase tracking-wider block mb-2">Prioridade</label>
            <div className="flex gap-2">
              {["Alta", "Média", "Baixa"].map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => set({ prioridade: p })}
                  className={`flex-1 h-9 rounded-xl text-sm font-semibold border transition ${
                    form.prioridade === p ? PRIORITY_COLOR[p] : "bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600"
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-2 pt-1">
            <button type="button" onClick={onClose} className="flex-1 h-10 rounded-xl border border-slate-700 text-sm text-slate-300 hover:bg-slate-800 transition">
              Cancelar
            </button>
            <button type="submit" className="flex-1 h-10 rounded-xl monyx-btn-primary text-white text-sm font-semibold transition inline-flex items-center justify-center gap-1.5">
              <Save className="h-4 w-4" /> Salvar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Adicionar Valor Modal ─────────────────────────────────────
function AdicionarValorModal({ open, meta, onClose, onSave }) {
  const [valor, setValor] = useState("");
  useEffect(() => { if (open) setValor(""); }, [open]);
  if (!open || !meta) return null;

  const valorN = Number(String(valor).replace(",", ".")) || 0;

  const submit = (e) => {
    e.preventDefault();
    if (valorN <= 0) return toast.error("Valor inválido");
    onSave(valorN);
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-sm bg-slate-900 border border-slate-700 rounded-2xl p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="font-display font-bold text-base mb-0.5">{meta.emoji || "🎯"} {meta.nome}</div>
        <div className="text-xs text-slate-400 mb-4">Adicionar valor guardado</div>
        <form onSubmit={submit} className="space-y-3">
          <input
            value={valor}
            onChange={(e) => setValor(e.target.value)}
            placeholder="0,00"
            inputMode="decimal"
            autoFocus
            className="w-full h-12 px-4 rounded-xl bg-slate-800 border border-slate-700 text-xl text-white placeholder:text-slate-500 focus:outline-none focus:border-emerald-500/40"
          />
          <div className="rounded-xl bg-slate-800 px-3 py-2 text-xs text-slate-400 flex items-center justify-between">
            <span>Novo total guardado</span>
            <span className="font-semibold text-white tabular-nums">{brl(Number(meta.atual || 0) + valorN)}</span>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="flex-1 h-10 rounded-xl border border-slate-700 text-sm text-slate-300 hover:bg-slate-800 transition">
              Cancelar
            </button>
            <button type="submit" className="flex-1 h-10 rounded-xl monyx-btn-primary text-white text-sm font-semibold transition">
              Adicionar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Meta card ────────────────────────────────────────────────
function MetaCard({ meta, onEdit, onAddValor, onDelete }) {
  const status  = calcStatus(meta);
  const pct     = Math.min(100, (Number(meta.atual) / Number(meta.alvo)) * 100) || 0;
  const meses   = mesesRestantes(meta.prazoData);
  const aporte  = calcAporte(meta);
  const cfg     = STATUS_CFG[status] || STATUS_CFG["Em andamento"];

  const prazoLabel = meses !== null
    ? (meses === 0 ? "Prazo encerrado" : `${meses} ${meses === 1 ? "mês restante" : "meses restantes"}`)
    : (meta.prazo || "—");

  return (
    <div className="rounded-2xl p-5 bg-slate-900 border border-slate-800 flex flex-col gap-3">
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-slate-800 grid place-items-center text-2xl shrink-0">
            {meta.emoji || "🎯"}
          </div>
          <div>
            <div className="font-semibold text-white text-sm leading-snug">{meta.nome}</div>
            {meta.prioridade && (
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full border mt-0.5 inline-block ${PRIORITY_COLOR[meta.prioridade] || PRIORITY_COLOR["Média"]}`}>
                {meta.prioridade}
              </span>
            )}
          </div>
        </div>
        <span className={`text-[10px] font-bold px-2 py-1 rounded-full border shrink-0 ${cfg.badge}`}>
          {cfg.icon} {status}
        </span>
      </div>

      {/* Progresso */}
      <div>
        <div className="flex items-baseline justify-between mb-1.5">
          <span className="text-xl font-display font-bold text-white tabular-nums">{brl(Number(meta.atual))}</span>
          <span className="text-xs text-slate-400">/ {brl(Number(meta.alvo))}</span>
        </div>
        <div className="h-2.5 bg-slate-800 rounded-full overflow-hidden">
          <div
            className={`h-full bg-gradient-to-r ${cfg.bar} rounded-full transition-all duration-500`}
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="mt-1.5 flex items-center justify-between text-xs">
          <span className="text-emerald-300 font-semibold">{pct.toFixed(0)}%</span>
          <span className="text-slate-400">{prazoLabel}</span>
        </div>
      </div>

      {/* Aporte */}
      {status !== "Concluída" && aporte > 0 && (
        <div className="rounded-xl bg-slate-800/60 px-3 py-2 flex items-center justify-between">
          <span className="text-xs text-slate-400">Aporte mensal necessário</span>
          <span className="text-xs font-bold text-white tabular-nums">{brl(aporte)}/mês</span>
        </div>
      )}

      {/* Ações */}
      <div className="flex gap-2 pt-1">
        <button
          onClick={onAddValor}
          className="flex-1 h-8 rounded-lg bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 text-xs font-semibold transition border border-emerald-500/20 hover:border-emerald-500/40 flex items-center justify-center gap-1"
        >
          <PlusCircle className="h-3.5 w-3.5" /> Adicionar valor
        </button>
        <button
          onClick={onEdit}
          className="h-8 w-8 grid place-items-center rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white transition"
          title="Editar"
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
        <button
          onClick={onDelete}
          className="h-8 w-8 grid place-items-center rounded-lg bg-slate-800 hover:bg-rose-500/20 text-slate-300 hover:text-rose-300 transition"
          title="Excluir"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

// ─── Exemplo card ─────────────────────────────────────────────
function ExemploCard({ template, onUse }) {
  return (
    <div className="rounded-2xl p-5 bg-slate-900 border border-slate-800 border-dashed flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-xl bg-slate-800 grid place-items-center text-2xl shrink-0">
          {template.emoji}
        </div>
        <div>
          <div className="font-semibold text-white text-sm">{template.nome}</div>
          <div className="text-xs text-slate-400">{template.descricao}</div>
        </div>
      </div>
      <div className="flex items-baseline justify-between">
        <span className="text-lg font-display font-bold text-slate-300 tabular-nums">{brl(template.alvo)}</span>
        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full border ${PRIORITY_COLOR[template.prioridade]}`}>
          {template.prioridade}
        </span>
      </div>
      <div className="h-2 bg-slate-800 rounded-full" />
      <button
        onClick={() => onUse(template)}
        className="w-full h-9 rounded-xl bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 text-xs font-semibold transition border border-emerald-500/20 hover:border-emerald-500/40"
      >
        Usar essa meta
      </button>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────
export default function Objetivos() {
  const { data, addObjetivo, updateObjetivo, removeObjetivo } = useAppData();
  const {
    objetivos, gastosFixos, gastosVariaveis, cartoes,
    entradas, selectedMonth, customCategories,
  } = data;

  const [modal,        setModal]        = useState({ open: false, item: null });
  const [addValorMeta, setAddValorMeta] = useState({ open: false, meta: null });
  const [filtro,       setFiltro]       = useState("Todas");
  const [ordenar,      setOrdenar]      = useState("Prazo");

  // ─── Saldo do mês + gastos por categoria ───────────────────
  const { saldoMes, totalGastosMes, gastosPorCat } = useMemo(() => {
    const GF   = filterByMonth(gastosFixos  || [], selectedMonth);
    const GV   = filterByMonth(gastosVariaveis || [], selectedMonth);
    const ENT  = filterByMonth(entradas     || [], selectedMonth);
    const TXm  = (cartoes || []).flatMap((c) => filterByMonth(c.transacoes || [], selectedMonth));
    const totalEntradasMes = sumValor(ENT);
    const totalGastosMes   = sumValor(GF) + sumValor(GV) + sumValor(TXm);
    const saldoMes = totalEntradasMes - totalGastosMes;

    const catMap = {};
    [...GF, ...GV, ...TXm].forEach((g) => {
      catMap[g.cat] = (catMap[g.cat] || 0) + Number(g.valor || 0);
    });
    const gastosPorCat = Object.entries(catMap)
      .map(([cat, total]) => ({ cat, nome: catById(cat, customCategories)?.name || cat, total }))
      .sort((a, b) => b.total - a.total);

    return { saldoMes, totalGastosMes, gastosPorCat };
  }, [gastosFixos, gastosVariaveis, entradas, cartoes, selectedMonth, customCategories]);

  // ─── Totais das metas ──────────────────────────────────────
  const totalAlvo  = objetivos.reduce((s, o) => s + Number(o.alvo  || 0), 0);
  const totalAtual = objetivos.reduce((s, o) => s + Number(o.atual || 0), 0);
  const pctGeral   = totalAlvo > 0 ? (totalAtual / totalAlvo) * 100 : 0;
  const sugestaoIdeal = saldoMes > 0 ? saldoMes * 0.2 : 0;

  // ─── Filtros e ordenação ───────────────────────────────────
  const metasFiltradas = useMemo(() => {
    const PRIORITY_ORD = { "Alta": 0, "Média": 1, "Baixa": 2 };
    let list = objetivos.map((o) => ({ ...o, _status: calcStatus(o) }));
    if (filtro !== "Todas") list = list.filter((o) => o._status === filtro);
    if (ordenar === "Prazo") {
      list.sort((a, b) => {
        if (!a.prazoData && !b.prazoData) return 0;
        if (!a.prazoData) return 1;
        if (!b.prazoData) return -1;
        return a.prazoData.localeCompare(b.prazoData);
      });
    } else if (ordenar === "Maior valor") {
      list.sort((a, b) => Number(b.alvo) - Number(a.alvo));
    } else if (ordenar === "Prioridade") {
      list.sort((a, b) => (PRIORITY_ORD[a.prioridade] ?? 2) - (PRIORITY_ORD[b.prioridade] ?? 2));
    }
    return list;
  }, [objetivos, filtro, ordenar]);

  // ─── Contagens para filtros ────────────────────────────────
  const counts = useMemo(() => {
    const c = { "Todas": objetivos.length, "Em andamento": 0, "Concluída": 0, "Atrasada": 0 };
    objetivos.forEach((o) => { c[calcStatus(o)] = (c[calcStatus(o)] || 0) + 1; });
    return c;
  }, [objetivos]);

  // ─── Dicas dinâmicas ──────────────────────────────────────
  const dicas = useMemo(() => {
    const tips = [];
    // 1. Maior categoria de gasto
    if (gastosPorCat.length > 0 && gastosPorCat[0].total > 0) {
      const top = gastosPorCat[0];
      const economia = top.total * 0.2;
      tips.push(
        `Você gastou ${brl(top.total)} em ${top.nome} este mês. Reduzindo 20%, guardaria mais ${brl(economia)}/mês nas suas metas.`
      );
    }
    // 2. Meta mais próxima de concluir
    const emAndamento = objetivos.filter((o) => calcStatus(o) === "Em andamento" && Number(o.alvo) > 0);
    if (emAndamento.length > 0 && saldoMes > 0) {
      const proxima = [...emAndamento].sort((a, b) => {
        const pa = Number(a.atual) / Number(a.alvo);
        const pb = Number(b.atual) / Number(b.alvo);
        return pb - pa;
      })[0];
      const falta = Math.max(0, Number(proxima.alvo) - Number(proxima.atual));
      if (falta > 0) {
        const mesesN = Math.ceil(falta / saldoMes);
        tips.push(
          `Com seu saldo de ${brl(saldoMes)}, você atinge "${proxima.nome}" em ~${mesesN} ${mesesN === 1 ? "mês" : "meses"}.`
        );
      }
    }
    // 3. Reserva de emergência
    const temReserva = objetivos.some((o) =>
      /reserva|emergên|emergenc/i.test(o.nome || "")
    );
    const reservaIdeal = totalGastosMes * 6;
    if (!temReserva && reservaIdeal > 0) {
      tips.push(
        `Sua reserva de emergência ideal é ${brl(reservaIdeal)} (6x seus gastos mensais). Você ainda não tem essa meta cadastrada.`
      );
    }
    // 4. Sugestão de aporte
    if (sugestaoIdeal > 0 && objetivos.length > 0 && tips.length < 3) {
      tips.push(
        `Você tem ${brl(saldoMes)} disponível este mês. Aportar 20% (${brl(sugestaoIdeal)}) aceleraria o progresso das suas metas.`
      );
    }
    return tips.slice(0, 3);
  }, [gastosPorCat, objetivos, saldoMes, totalGastosMes, sugestaoIdeal]);

  // ─── Handlers ─────────────────────────────────────────────
  const handleSave = (payload) => {
    if (modal.item && modal.item.id) {
      updateObjetivo(modal.item.id, payload);
      toast.success("Meta atualizada!");
    } else {
      addObjetivo(payload);
      toast.success("Meta criada!");
    }
    setModal({ open: false, item: null });
  };

  const handleAddValor = (v) => {
    const novoAtual = Number(addValorMeta.meta.atual || 0) + v;
    updateObjetivo(addValorMeta.meta.id, { atual: novoAtual });
    toast.success(`${brl(v)} adicionado à meta!`);
    setAddValorMeta({ open: false, meta: null });
  };

  const handleDelete = (id) => {
    if (window.confirm("Remover esta meta?")) {
      removeObjetivo(id);
      toast.success("Meta removida");
    }
  };

  const FILTROS = [
    { id: "Todas",        label: `Todas (${counts["Todas"]})`               },
    { id: "Em andamento", label: `Em andamento (${counts["Em andamento"]})` },
    { id: "Concluída",    label: `Concluídas (${counts["Concluída"]})`       },
    { id: "Atrasada",     label: `Atrasadas (${counts["Atrasada"]})`         },
  ];

  return (
    <AppLayout>
      <PageHeader title="Meus Objetivos" subtitle="Progresso e planejamento das suas metas" testid="objetivos" />
      <div className="max-w-6xl mx-auto px-5 md:px-8 py-6 space-y-6">

        {/* ── Header bar ── */}
        <div className="flex flex-col gap-3">
          {/* Filtros — scroll horizontal se necessário */}
          <div className="flex gap-1 p-1 bg-slate-900 border border-slate-800 rounded-2xl overflow-x-auto">
            {FILTROS.map((f) => (
              <button
                key={f.id}
                onClick={() => setFiltro(f.id)}
                className={`px-3 h-8 rounded-xl text-xs font-medium transition whitespace-nowrap flex-shrink-0 ${
                  filtro === f.id ? "bg-slate-800 text-white" : "text-slate-400 hover:text-white"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="flex items-center justify-between gap-2">
            {/* Ordenação */}
            <select
              value={ordenar}
              onChange={(e) => setOrdenar(e.target.value)}
              className="h-10 px-3 rounded-xl bg-slate-900 border border-slate-800 text-sm text-slate-300 focus:outline-none"
            >
              <option value="Prazo">Prazo mais próximo</option>
              <option value="Maior valor">Maior valor</option>
              <option value="Prioridade">Prioridade</option>
            </select>
            <button
              onClick={() => setModal({ open: true, item: null })}
              className="monyx-btn-primary text-white rounded-full h-10 px-4 text-sm font-semibold inline-flex items-center gap-2 shrink-0"
              data-testid="btn-nova-meta"
            >
              <Plus className="h-4 w-4" /> Nova meta
            </button>
          </div>
        </div>

        {/* ── Empty state + exemplos ── */}
        {objetivos.length === 0 ? (
          <div className="space-y-4">
            <div className="rounded-2xl p-6 bg-slate-900 border border-slate-800 text-center">
              <Target className="h-10 w-10 mx-auto text-slate-600 mb-2" />
              <p className="text-slate-300 font-semibold text-sm">Nenhuma meta cadastrada ainda.</p>
              <p className="text-slate-500 text-xs mt-1">Use uma sugestão abaixo ou crie do zero com o botão acima.</p>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              {TEMPLATES.map((t) => (
                <ExemploCard
                  key={t.nome}
                  template={t}
                  onUse={(tpl) => setModal({ open: true, item: { ...tpl, id: null } })}
                />
              ))}
            </div>
          </div>
        ) : (
          <>
            {metasFiltradas.length === 0 ? (
              <div className="rounded-2xl p-8 bg-slate-900 border border-slate-800 text-center text-slate-400 text-sm">
                Nenhuma meta com o filtro "{filtro}".
              </div>
            ) : (
              <div className="grid md:grid-cols-3 gap-4">
                {metasFiltradas.map((o) => (
                  <MetaCard
                    key={o.id}
                    meta={o}
                    onEdit={() => setModal({ open: true, item: o })}
                    onAddValor={() => setAddValorMeta({ open: true, meta: o })}
                    onDelete={() => handleDelete(o.id)}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* ── Planejamento + Inteligência ── */}
        <div className="grid md:grid-cols-2 gap-4">
          {/* Planejamento Geral */}
          <div className="rounded-2xl p-6 bg-gradient-to-br from-blue-600 to-blue-800 text-white" data-testid="planejamento-geral">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-semibold opacity-90 mb-4">
              <TrendingUp className="h-4 w-4" /> Planejamento Geral
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-[11px] opacity-80">Saldo disponível</div>
                <div className="text-xl font-display font-bold tabular-nums">{brl(Math.max(0, saldoMes))}</div>
              </div>
              <div>
                <div className="text-[11px] opacity-80">Sugestão ideal / mês</div>
                <div className="text-xl font-display font-bold tabular-nums">{brl(sugestaoIdeal)}</div>
              </div>
              <div>
                <div className="text-[11px] opacity-80">Já guardado (total)</div>
                <div className="text-xl font-display font-bold tabular-nums">{brl(totalAtual)}</div>
              </div>
              <div>
                <div className="text-[11px] opacity-80">Meta total</div>
                <div className="text-xl font-display font-bold tabular-nums">{brl(totalAlvo)}</div>
              </div>
            </div>
            <div className="mt-4">
              <div className="flex items-center justify-between text-xs opacity-80 mb-1.5">
                <span>Progresso geral</span>
                <span className="font-bold">{pctGeral.toFixed(1)}%</span>
              </div>
              <div className="h-2.5 bg-white/15 rounded-full overflow-hidden">
                <div className="h-full bg-emerald-300 rounded-full transition-all" style={{ width: `${pctGeral}%` }} />
              </div>
            </div>
          </div>

          {/* Inteligência Conselheira */}
          <div className="rounded-2xl p-6 bg-slate-900 border border-slate-800" data-testid="inteligencia-conselheira">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-emerald-300 font-semibold mb-4">
              <Sparkles className="h-4 w-4" /> Inteligência Conselheira
            </div>
            {dicas.length === 0 ? (
              <p className="text-sm text-slate-400">
                Adicione metas e lançamentos para receber dicas personalizadas.
              </p>
            ) : (
              <ul className="space-y-3">
                {dicas.map((dica, i) => (
                  <li key={i} className="flex gap-3 text-sm text-slate-300">
                    <span className="h-5 w-5 rounded-full bg-emerald-500/20 text-emerald-300 grid place-items-center text-[10px] shrink-0 mt-0.5 font-bold">
                      {i + 1}
                    </span>
                    {dica}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* ── Modais ── */}
      <NovaMetaModal
        open={modal.open}
        onClose={() => setModal({ open: false, item: null })}
        item={modal.item}
        onSave={handleSave}
      />
      <AdicionarValorModal
        open={addValorMeta.open}
        meta={addValorMeta.meta}
        onClose={() => setAddValorMeta({ open: false, meta: null })}
        onSave={handleAddValor}
      />
    </AppLayout>
  );
}
